import { selectedScreen } from './nativeScreens.js'
import { addLeagueHub, hubCatalog } from './nativeHubModel.js'
import { useCollectionPreviews } from './useCollectionPreview'
import { useReferencedEvents } from './useReferencedEvents'
import { projectPreviewInventory } from './previewInventory'
import { COLLECTION_INVENTORY_ID, newCollection, collectionPersonas } from './collectionModel'
import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { nativeSportsbookApi } from '@/services/api'
import { normalizeRuntimePreviewCatalog, runtimePreviewCatalogState } from './nativeRuntimeCatalog'
import { PREVIEW_CATALOG } from './nativePreviewModel'
import { catalogSourceLabel, isLiveSource, liveSourcesFrom } from './catalogSources'
import { createExperience } from './experienceModel'
import { COMPONENT_REGISTRY, DEFAULT_MANIFEST, createCard, createComponent, publishableManifest, reconcileManifestCapabilities, visiblePreviewComponents } from './nativeSportsbookManifest'

import { createNativeDraftState, draftFingerprint, nativeDraftReducer } from './nativeDraftHistory'
import { resolveDeviceInspection } from './devicePreviewModel'
import { draftPreviewFingerprint } from './draftPreviewModel'
import { duplicateSection, duplicateFeaturedCard } from './nativeDuplication'
import { copyNativeContent, pasteNativeContent } from './nativeClipboard'
import { bindExistingSection, insertReusableSection, resetSectionOverride, updateReusableInstances } from './nativeSectionReuse'

function setPath(target, path, value) {
  const segments = path.split('.')
  let node = target
  for (const segment of segments.slice(0, -1)) {
    if (!node[segment]) node[segment] = {}
    node = node[segment]
  }
  const key = segments.at(-1)
  if (value === undefined || value === '' || value === null) delete node[key]
  else node[key] = value
}

export function useNativeSportsbookWorkspace(slug) {
  const queryClient = useQueryClient()
  const [editor, dispatch] = useReducer(nativeDraftReducer, DEFAULT_MANIFEST, createNativeDraftState)
  const { draft, selected, validation, notice, noticeTone, selection: editorSelection } = editor
  const screen = selectedScreen(draft, editorSelection)
  const selectedComponentId = editorSelection.componentId
  const selectedCardId = editorSelection.cardId
  const dirty = draftFingerprint(draft, editor.bindings) !== editor.savedFingerprint
  const setDraft = (update, label = 'edit', group) => dispatch({ type: 'edit', update, label, group })
  const setSelected = (value) => dispatch({ type: 'selected', value })
  const setValidation = (value) => dispatch({ type: 'validation', value })
  const setNotice = (value) => dispatch({ type: 'notice', value })
  const setNoticeTone = (value) => dispatch({ type: 'notice-tone', value })
  const setSelectedComponentId = (componentId) => dispatch({ type: 'select', selection: { componentId, cardId: null } })
  const setSelectedCardId = (cardId) => dispatch({ type: 'select', selection: { cardId } })
  const setEditorSelection = (selection) => dispatch({ type: 'select', selection })
  const loadManifest = (manifest, notice) => dispatch({ type: 'load', manifest, notice })
  const [jurisdiction, setJurisdiction] = useState('US-NJ')
  const [sessionState, setSessionState] = useState('anonymous')
  const [previewPersona, setPreviewPersona] = useState(null)
  const [previewInventory, setPreviewInventory] = useState('current')
  const [publishChannel, setPublishChannel] = useState('staging')
  const [publishReason, setPublishReason] = useState('')
  const [rollbackTarget, setRollbackTarget] = useState(null)
  const [rollbackReason, setRollbackReason] = useState('')
  const [requestedCatalogSource, setCatalogSource] = useState('sample')
  const [inspectionFocus, setInspectionFocus] = useState(null)
  const [placeholderExperience] = useState(() => createExperience())
  const [sentDraftPreviews, setSentDraftPreviews] = useState({})
  const inspectedSelections = useRef(new Map())
  const manifestsQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'manifests'], queryFn: () => nativeSportsbookApi.listManifests(slug), enabled: Boolean(slug) })
  // The API binds a runtime per tenant and Kambi tier; without one every live
  // preview 503s, so a live request resolves to sample games until it exists.
  const liveSources = useMemo(() => liveSourcesFrom(manifestsQuery.data), [manifestsQuery.data])
  const catalogSource = liveSources.includes(requestedCatalogSource) ? requestedCatalogSource : 'sample'
  const liveCatalog = useQuery({
    queryKey: ['native-live-preview', slug, catalogSource, jurisdiction], enabled: isLiveSource(catalogSource),
    queryFn: async ({ signal }) => {
      const value = await nativeSportsbookApi.previewCollection(slug, { ...newCollection({}, COLLECTION_INVENTORY_ID), limit: 20 }, signal, null, jurisdiction, catalogSource)
      if (value.state === 'unavailable') throw new Error(`${catalogSourceLabel(catalogSource)} games are unavailable. Retry when the connection is restored.`)
      return normalizeRuntimePreviewCatalog({ ...value, freshness: value.state === 'cached' ? 'stale' : 'replicated' })
    },
    refetchInterval: 15000, retry: false,
  })
  const baseCatalog = isLiveSource(catalogSource) ? runtimePreviewCatalogState(liveCatalog) : PREVIEW_CATALOG
  const personas = useMemo(() => collectionPersonas(baseCatalog), [baseCatalog])
  const activePersona = sessionState === 'authenticated' ? previewPersona : null
  const previewScenario = useMemo(() => ({ schema_version: '1.0.0', regulation: jurisdiction, session: sessionState, persona: activePersona, inventory: previewInventory }), [jurisdiction, sessionState, activePersona, previewInventory])
  const collectionResults = useCollectionPreviews(slug, visiblePreviewComponents(draft, jurisdiction, sessionState, screen.id), catalogSource, PREVIEW_CATALOG, activePersona, screen.context, jurisdiction)
  const pageEvents = [...baseCatalog.events, ...Object.values(collectionResults).flatMap((result) => result.events)]
  const referencedEvents = useReferencedEvents(slug, catalogSource, jurisdiction, screen.components || [], pageEvents)
  const previewInventoryCatalog = projectPreviewInventory({ ...baseCatalog, collection_results: collectionResults, events: [...new Map([...pageEvents, ...referencedEvents].map((event) => [event.id, event])).values()] }, previewInventory)
  // source/regulation let editor pickers search the inventory the preview renders from.
  const previewCatalog = { ...hubCatalog(previewInventoryCatalog, screen.context), source: catalogSource, regulation: jurisdiction, hub_routes: draft.screens.filter((item) => item.context).map((item) => ({ value: item.route, label: item.title })) }

  const registryQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'component-registry'], queryFn: () => nativeSportsbookApi.getComponentRegistry(slug), enabled: Boolean(slug), staleTime: 5 * 60 * 1000 })
  const previewDevicesQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'preview-devices'], queryFn: () => nativeSportsbookApi.listPreviewDevices(slug), enabled: Boolean(slug), refetchInterval: 5000 })
  const refreshPreviewDevices = () => queryClient.invalidateQueries({ queryKey: ['native-sportsbook', slug, 'preview-devices'] })
  const pairDeviceMutation = useMutation({ mutationFn: ({ platform, channel }) => nativeSportsbookApi.createPreviewDevice(slug, platform, channel), onSuccess: refreshPreviewDevices })
  const revokeDeviceMutation = useMutation({ mutationFn: (id) => nativeSportsbookApi.updatePreviewDevice(slug, id, { revoke: true }), onSuccess: refreshPreviewDevices })
  const rehearseDevicesMutation = useMutation({
    scope: { id: `native-preview-command:${slug}` },
    mutationFn: ({ id, phase }) => nativeSportsbookApi.updatePreviewDevice(slug, id, { rehearsal_phase: phase }),
    onSuccess: refreshPreviewDevices,
  })
  const inspectDeviceMutation = useMutation({
    scope: { id: `native-preview-command:${slug}` },
    mutationFn: async ({ id, enabled }) => {
      if (enabled && inspectionFocus && inspectionFocus.id !== id) {
        try { await nativeSportsbookApi.updatePreviewDevice(slug, inspectionFocus.id, { inspection_enabled: false }) }
        catch (error) { if (error.response?.status !== 404) throw error }
      }
      await nativeSportsbookApi.updatePreviewDevice(slug, id, { inspection_enabled: enabled })
      return nativeSportsbookApi.listPreviewDevices(slug)
    },
    onSuccess: (data, { id, enabled }) => {
      queryClient.setQueryData(['native-sportsbook', slug, 'preview-devices'], data)
      const device = data.devices.find((item) => item.id === id)
      setInspectionFocus(enabled && device ? { id, revision: device.inspection_revision } : null)
      setNotice(enabled ? `Tap a section on ${device?.platform === 'ios' ? 'iPhone' : 'Android'} to select it here.` : 'Device inspection stopped.')
      setNoticeTone('neutral')
    },
  })
  const publicationsQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'publications'], queryFn: () => nativeSportsbookApi.listPublications(slug), enabled: Boolean(slug) })
  const themeQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'theme'], queryFn: () => nativeSportsbookApi.getTheme(slug), enabled: Boolean(slug) })
  const manifests = useMemo(() => manifestsQuery.data?.manifests || [], [manifestsQuery.data?.manifests])
  const publications = useMemo(() => publicationsQuery.data?.publications || [], [publicationsQuery.data?.publications])
  const registry = registryQuery.data || COMPONENT_REGISTRY
  const currentTheme = themeQuery.data?.current ?? null
  const themeOutdated = Boolean(currentTheme && selected && (selected.theme_revision ?? 0) < currentTheme.revision)
  const document = useMemo(() => {
    const base = publishableManifest(draft)
    return currentTheme ? { ...base, theme: currentTheme.document } : base
  }, [draft, currentTheme])
  const previewFingerprint = useMemo(() => draftPreviewFingerprint(document, editor.bindings, selected, previewScenario), [document, editor.bindings, selected, previewScenario])
  const sendingDraftPreview = useMutation({
    scope: { id: `native-preview-command:${slug}` },
    mutationFn: ({ id, snapshot }) => nativeSportsbookApi.sendDraftPreview(slug, id, snapshot),
    onSuccess: ({ preview }, request) => {
      setSentDraftPreviews((current) => ({ ...current, [`${slug}:${request.id}`]: { editorSnapshotId: preview.editor_snapshot_id, fingerprint: request.fingerprint } }))
      refreshPreviewDevices()
    },
  })
  const sendDraftPreviewMutation = { ...sendingDraftPreview, mutate: (id, options) => {
    sendingDraftPreview.mutate({ id, fingerprint: previewFingerprint, snapshot: structuredClone({
      draft_id: selected?.id, draft_revision: selected?.revision, editor_snapshot_id: crypto.randomUUID(), document, section_bindings: editor.bindings, scenario: previewScenario,
    }) }, options)
  } }
  const returnPublishedMutation = useMutation({
    scope: { id: `native-preview-command:${slug}` },
    mutationFn: (id) => nativeSportsbookApi.updatePreviewDevice(slug, id, { return_published: true }),
    onSuccess: refreshPreviewDevices,
  })
  const components = selectedScreen(draft, { screenId: screen.id }).components
  const selectedComponentIndex = components.findIndex((component) => component.id === selectedComponentId)
  const selectedComponent = selectedComponentIndex === -1 ? null : components[selectedComponentIndex]
  const selectedCardIndex = selectedComponent?.cards ? selectedComponent.cards.findIndex((card) => card.id === selectedCardId) : -1
  const selectedCard = selectedCardIndex === -1 ? null : selectedComponent.cards[selectedCardIndex]
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['native-sportsbook', slug] })
  const touch = () => setValidation(null)

  useEffect(() => {
    if (!selected && !dirty && manifests[0]) dispatch({ type: 'load', manifest: manifests[0] })
  }, [dirty, manifests, selected])

  useEffect(() => {
    if (!inspectionFocus) return
    const device = previewDevicesQuery.data?.devices.find((item) => item.id === inspectionFocus?.id)
    const pick = device?.report?.selection
    if (!pick || device.inspection_revision < inspectionFocus.revision || inspectedSelections.current.get(device.id) === pick.id) return
    const result = resolveDeviceInspection(device, selected, draft)
    if (!result) return
    inspectedSelections.current.set(device.id, pick.id)
    if (result.selection) dispatch({ type: 'select', selection: result.selection })
    const target = device.draft_preview || device.requested
    const revision = target?.revision !== selected?.revision ? ` Phone shows r${target?.revision}; editing r${selected?.revision}.` : ''
    dispatch({ type: 'notice', value: result.error || `Selected ${result.label} from ${device.platform === 'ios' ? 'iPhone' : 'Android'}.${revision}` })
    dispatch({ type: 'notice-tone', value: result.error ? 'warning' : 'neutral' })
  }, [previewDevicesQuery.data, inspectionFocus, selected, draft])

  const validateMutation = useMutation({
    mutationFn: ({ id }) => nativeSportsbookApi.validateManifest(slug, id),
    onSuccess: (result, request) => dispatch({ type: 'validated', result, request }),
    onError: (error, request) => dispatch({ type: 'validated', error, request }),
  })
  const saving = useMutation({
    mutationFn: ({ id, document, bindings }) => id ? nativeSportsbookApi.updateManifest(slug, id, document, bindings) : nativeSportsbookApi.createManifest(slug, document, bindings),
    onSuccess: (manifest, request) => {
      dispatch({ type: 'saved', manifest, request })
      refresh()
      validateMutation.mutate({ id: manifest.id, revision: manifest.revision, epoch: request.epoch })
    },
  })
  const saveMutation = { ...saving, mutate: (_variables, options) => {
    dispatch({ type: 'finish-group' })
    saving.mutate({ id: selected?.id, document, draft, bindings: editor.bindings, epoch: editor.epoch }, options)
  } }
  const requestApprovalMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.requestApproval(slug, selected.id, publishReason),
    onSuccess: (manifest) => { setSelected((current) => current?.id === manifest.id ? manifest : current); setNotice(`Revision ${manifest.revision} is awaiting an independent production approval.`); setNoticeTone('success'); refresh() },
  })
  const approveMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.approveManifest(slug, selected.id),
    onSuccess: (manifest) => { setSelected((current) => current?.id === manifest.id ? manifest : current); setNotice(`Approved revision ${manifest.revision} for production.`); setNoticeTone('success'); refresh() },
  })
  const publishMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.publishManifest(slug, selected.id, publishChannel, publishReason),
    onSuccess: (publication) => {
      setNotice(`Published ${publication.channel} manifest v${publication.manifest_version} · ${publication.content_sha256.slice(0, 12)}.`)
      setNoticeTone('success')
      if (publication.channel === 'production') setSelected((current) => current?.manifest_id === publication.manifest_id && current.revision === publication.manifest_revision ? { ...current, status: 'published' } : current)
      refresh()
    },
  })
  const rollbackMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.rollbackPublication(slug, rollbackTarget.id, rollbackReason),
    onSuccess: (publication) => { setNotice(`Rolled back to manifest v${publication.manifest_version} · ${publication.content_sha256.slice(0, 12)}.`); setNoticeTone('success'); setRollbackTarget(null); refresh() },
  })
  const setSafeMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.setSafePublication(slug, rollbackTarget.id, rollbackReason),
    onSuccess: (publication) => { setNotice(`Known-safe target set to manifest v${publication.manifest_version}.`); setNoticeTone('success'); setRollbackTarget(null); refresh() },
  })
  const safeModeMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.activateSafeMode(slug, rollbackReason),
    onSuccess: (publication) => { setNotice(`Safe Home active · manifest v${publication.manifest_version}.`); setNoticeTone('success'); refresh() },
  })
  const reconcileMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.reconcilePublications(slug),
    onSuccess: () => { setNotice('Pending publication commands reconciled.'); setNoticeTone('success'); refresh() },
  })
  const updateNavigation = (navigation) => {
    setDraft((current) => { const next = structuredClone(current); selectedScreen(next, { screenId: screen.id }).navigation = navigation; return reconcileManifestCapabilities(next, registry) })
    touch()
  }
  const updateComponent = (index, patch) => {
    setDraft((current) => reconcileManifestCapabilities((() => { const next = structuredClone(current); selectedScreen(next, { screenId: screen.id }).components[index] = { ...selectedScreen(next, { screenId: screen.id }).components[index], ...patch }; return next })(), registry))
    touch()
  }
  const updateComponentField = (index, path, value) => {
    setDraft((current) => reconcileManifestCapabilities((() => {
      const next = structuredClone(current)
      setPath(selectedScreen(next, { screenId: screen.id }).components[index], path, value)
      return next
    })(), registry), 'section settings', `component:${selectedScreen(draft, { screenId: screen.id }).components[index].id}:${path}`)
    touch()
  }
  const moveComponentTo = (index, requestedTarget) => {
    const list = selectedScreen(draft, { screenId: screen.id }).components
    const complianceIndex = list.findIndex((component) => component.type === 'compliance_notice')
    const limit = complianceIndex === -1 ? list.length - 1 : complianceIndex - 1
    const target = Math.max(0, Math.min(requestedTarget, limit))
    if (index === target || !list[index] || list[index].type === 'compliance_notice') return false
    setDraft((current) => {
      const next = structuredClone(current)
      const [component] = selectedScreen(next, { screenId: screen.id }).components.splice(index, 1)
      selectedScreen(next, { screenId: screen.id }).components.splice(target, 0, component)
      return next
    })
    touch()
    return true
  }
  const cardLimit = (component) => registry.components.find((definition) => definition.type === component?.type)?.editor?.cards?.max_cards ?? 0
  const addCard = (componentIndex, kind, requestedIndex) => {
    const component = selectedScreen(draft, { screenId: screen.id }).components[componentIndex]
    if (!component?.cards || component.cards.length >= cardLimit(component)) return null
    const card = createCard(kind)
    if (screen.context && kind === 'event_spotlight') {
      const event = previewCatalog.events[0]
      card.event_selector = event ? { mode: 'manual', event_id: event.id } : { mode: 'next_up' }
      card.headline = event ? event.name.slice(0, 80) : `${screen.title} matchup`.slice(0, 80)
    }
    const selectedIndex = component.cards.findIndex((candidate) => candidate.id === selectedCardId)
    const fallback = selectedIndex === -1 ? component.cards.length : selectedIndex + 1
    const target = Math.max(0, Math.min(requestedIndex ?? fallback, component.cards.length))
    setDraft((current) => {
      const next = structuredClone(current)
      selectedScreen(next, { screenId: screen.id }).components[componentIndex].cards.splice(target, 0, card)
      return next
    })
    setSelectedComponentId(component.id)
    setSelectedCardId(card.id)
    touch()
    setNotice('Card added. Review its settings, then save a new revision.')
    return card
  }
  const updateCardField = (componentIndex, cardIndex, path, value) => {
    setDraft((current) => {
      const next = structuredClone(current)
      setPath(selectedScreen(next, { screenId: screen.id }).components[componentIndex].cards[cardIndex], path, value)
      return next
    }, 'card settings', `card:${selectedScreen(draft, { screenId: screen.id }).components[componentIndex].cards[cardIndex].id}:${path}`)
    touch()
  }
  const moveCardTo = (componentIndex, index, requestedTarget) => {
    const cards = selectedScreen(draft, { screenId: screen.id }).components[componentIndex]?.cards || []
    const target = Math.max(0, Math.min(requestedTarget, cards.length - 1))
    if (index === target || !cards[index]) return false
    setDraft((current) => {
      const next = structuredClone(current)
      const list = selectedScreen(next, { screenId: screen.id }).components[componentIndex].cards
      const [card] = list.splice(index, 1)
      list.splice(target, 0, card)
      return next
    })
    touch()
    return true
  }
  const removeCard = (componentIndex, cardIndex) => {
    const component = selectedScreen(draft, { screenId: screen.id }).components[componentIndex]
    if (!component?.cards || component.cards.length <= 1) return
    const removed = component.cards[cardIndex]
    setDraft((current) => {
      const next = structuredClone(current)
      selectedScreen(next, { screenId: screen.id }).components[componentIndex].cards.splice(cardIndex, 1)
      return next
    })
    if (removed?.id === selectedCardId) setSelectedCardId(component.cards.filter((_, index) => index !== cardIndex)[Math.max(0, cardIndex - 1)]?.id ?? null)
    touch()
  }
  const moveComponent = (index, direction) => moveComponentTo(index, index + direction)
  const duplicateComponent = (index) => {
    try {
      const result = duplicateSection(draft, selectedScreen(draft, { screenId: screen.id }).components[index]?.id, registry)
      setDraft(result.document, 'duplicate section')
      setSelectedComponentId(result.component.id)
      setNoticeTone('success')
      setNotice('Section duplicated. The copy is selected; edit it or Undo to remove it.')
      return result.component
    } catch (error) { setNotice(error.message); setNoticeTone('warning'); return null }
  }
  const duplicateCard = (componentIndex, cardIndex) => {
    try {
      const component = selectedScreen(draft, { screenId: screen.id }).components[componentIndex]
      const result = duplicateFeaturedCard(draft, component?.id, component?.cards?.[cardIndex]?.id, registry)
      setDraft(result.document, 'duplicate card')
      setSelectedComponentId(result.component.id)
      setSelectedCardId(result.card.id)
      setNoticeTone('success')
      setNotice('Card duplicated. The copy is selected; edit it or Undo to remove it.')
      return result.card
    } catch (error) { setNotice(error.message); setNoticeTone('warning'); return null }
  }
  const addComponent = (type, requestedIndex) => {
    if (type === 'competition_markets' && screen.context?.kind !== 'competition') {
      setNotice('Competition markets can only be added to a hub screen with a competition context.')
      setNoticeTone('warning')
      return null
    }
    const component = createComponent(type, screen.context)
    if (screen.context && type === 'event_list') {
      component.collection = { ...newCollection(), sports: [screen.context.sport], competitions: [screen.context.competition] }
      delete component.event_ids; delete component.sport_ids
    }
    if (screen.context && type === 'hero') component.action = { label: 'Browse games', route: screen.route }
    if (screen.context && type === 'sports_search') { component.title = 'Search sportsbook'; component.placeholder = `Search ${screen.title}`.slice(0, 80); component.suggested_queries = [screen.context.competition].filter((query) => query.length >= 2 && query.length <= 60) }
    if (screen.context && type === 'live_game_momentum') component.event_selector = { mode: 'first_live', sport_ids: [screen.context.sport.toUpperCase()] }
    if (screen.context && type === 'featured_showcase') {
      const promotion = createCard('promotion')
      promotion.headline = `${screen.title} highlights`.slice(0, 80)
      promotion.body = 'Explore the league and find your next matchup.'
      promotion.badge = 'FEATURED'
      promotion.action = { label: 'Browse games', route: screen.route }
      component.cards = [promotion]
    }
    setDraft((current) => {
      const next = structuredClone(current)
      const list = selectedScreen(next, { screenId: screen.id }).components
      const complianceIndex = list.findIndex((candidate) => candidate.type === 'compliance_notice')
      const limit = complianceIndex === -1 ? list.length : complianceIndex
      const fallback = selectedComponentIndex === -1 ? limit : Math.min(selectedComponentIndex + 1, limit)
      const target = Math.max(0, Math.min(requestedIndex ?? fallback, limit))
      list.splice(target, 0, component)
      return reconcileManifestCapabilities(next, registry)
    })
    setSelectedComponentId(component.id)
    touch()
    setNotice('Section added. Review its settings, then save a new revision.')
    return component
  }
  const removeComponent = (index) => {
    setDraft((current) => {
      const list = selectedScreen(current, { screenId: screen.id }).components
      const definition = registry.components.find((candidate) => candidate.type === list[index]?.type)
      if (!definition?.policy.removable) return current
      const next = structuredClone(current)
      selectedScreen(next, { screenId: screen.id }).components.splice(index, 1)
      for (const scene of next.experience?.home_scenes || []) {
        scene.component_order = scene.component_order.filter((id) => id !== list[index].id)
        scene.hidden_component_ids = scene.hidden_component_ids.filter((id) => id !== list[index].id)
      }
      return reconcileManifestCapabilities(next, registry)
    })
    touch()
  }
  // Opening a game page is read-only; the default experience joins the draft on the first edit.
  const experience = draft.experience ?? placeholderExperience
  const updateExperience = (experience) => {
    setDraft((current) => {
      const next = structuredClone(current)
      if (experience) next.experience = experience
      else delete next.experience
      return reconcileManifestCapabilities(next, registry)
    })
    touch()
  }
  const copyContent = (experience = draft.experience) => copyNativeContent({ ...draft, ...(experience ? { experience } : {}) }, editorSelection, slug, registry)
  const pasteContent = (text, destination = {}) => {
    const experience = draft.experience ?? destination.experience
    const result = pasteNativeContent({ ...draft, ...(experience ? { experience } : {}) }, editorSelection, text, slug, registry, undefined, destination)
    setDraft(result.document, `paste ${result.kind === 'home_variation' ? 'Home variation' : result.kind.replaceAll('_', ' ')}`)
    setEditorSelection(result.selection)
    setNotice(`${result.name} pasted. The copy is selected.`)
    setNoticeTone('success')
    return result
  }
  const editorRef = useRef(editor)
  editorRef.current = editor
  const reusableSnapshot = () => ({ epoch: editor.epoch, fingerprint: draftFingerprint(draft, editor.bindings) })
  const applyReusableEdit = (operation, label, request) => {
    const current = editorRef.current
    if (request && (request.epoch !== current.epoch || request.fingerprint !== draftFingerprint(current.draft, current.bindings))) {
      setNotice('The library version was saved. Newer edits were kept; review the update to apply it.')
      setNoticeTone('warning')
      return false
    }
    const result = operation(current)
    dispatch({ type: 'edit', update: result.draft ?? current.draft, bindings: result.bindings ?? current.bindings, label })
    if (result.componentId) setSelectedComponentId(result.componentId)
    return true
  }
  const bindSection = (componentId, section, request) => applyReusableEdit((current) => ({ bindings: bindExistingSection(current.draft, current.bindings, componentId, section) }), 'save as reusable section', request)
  const insertSection = (section) => applyReusableEdit((current) => insertReusableSection(current.draft, current.bindings, section, current.selection.componentId, registry, undefined, current.selection.screenId), 'insert reusable section')
  const updateSectionInstances = (section, request) => applyReusableEdit((current) => updateReusableInstances(current.draft, current.bindings, section, registry), 'update reusable sections', request)
  const detachSection = (componentId) => applyReusableEdit((current) => ({ bindings: current.bindings.filter((item) => item.component_id !== componentId) }), 'detach reusable section')
  const resetOverride = (componentId, field) => applyReusableEdit((current) => ({ draft: resetSectionOverride(current.draft, current.bindings, componentId, field) }), `reset ${field ? field.replaceAll('_', ' ') : 'section'} override`)
  const selectComponent = (id) => { setSelectedComponentId(id); setSelectedCardId(null) }
  const selectCard = (componentId, cardId) => { setSelectedComponentId(componentId); setSelectedCardId(cardId) }
  const selectManifest = (manifest) => {
    loadManifest(manifest, `Loaded revision ${manifest.revision}.`)
  }
  const selectScreen = (screenId) => {
    const target = selectedScreen(draft, { screenId })
    setEditorSelection({ screenId: target.id, surface: 'home', componentId: target.components[0]?.id ?? null, cardId: null, sceneId: null })
  }
  const createLeagueHub = (choice) => {
    const result = addLeagueHub(draft, choice, baseCatalog)
    setDraft(result.document, 'add league hub')
    setEditorSelection({ screenId: result.screen.id, surface: 'home', componentId: result.screen.components[0].id, cardId: null, sceneId: null })
    setNotice(`${result.screen.title} hub added. Configure its games or add a campaign.`)
  }
  const requestError = [saveMutation, validateMutation, requestApprovalMutation, approveMutation, publishMutation, rollbackMutation, setSafeMutation, safeModeMutation, reconcileMutation].find((mutation) => mutation.error)?.error

  return {
    screen, selectScreen, createLeagueHub, baseCatalog,
    sendDraftPreviewMutation, returnPublishedMutation, previewFingerprint, sentDraftPreview: (id) => sentDraftPreviews[`${slug}:${id}`],
    sectionBindings: editor.bindings, reusableSnapshot, bindSection, insertSection, updateSectionInstances, detachSection, resetOverride,
    copyContent, pasteContent,
    editorSelection, setEditorSelection,
    canUndo: editor.past.length > 0, canRedo: editor.future.length > 0,
    undo: () => dispatch({ type: 'undo' }), redo: () => dispatch({ type: 'redo' }),
    finishEditGroup: () => dispatch({ type: 'finish-group' }),
    updateExperience,
    previewDevicesQuery, pairDeviceMutation, revokeDeviceMutation, rehearseDevicesMutation, inspectDeviceMutation, inspectionDeviceId: inspectionFocus?.id,
    previewCatalog, catalogSource, setCatalogSource, liveCatalog, liveSources, experience,
    personas, previewPersona, setPreviewPersona, previewScenario, previewInventory, setPreviewInventory,
    draft, selected, dirty, validation, jurisdiction, sessionState, publishChannel, publishReason,
    rollbackTarget, rollbackReason, notice, noticeTone, registryQuery, manifestsQuery, publicationsQuery, manifests, publications, registry, document,
    themeQuery, currentTheme, themeOutdated,
    selectedComponentId, selectedComponent, selectedComponentIndex, selectedCardId, selectedCard, selectedCardIndex,
    requestError, saveMutation, validateMutation, requestApprovalMutation, approveMutation, publishMutation, rollbackMutation, setSafeMutation, safeModeMutation, reconcileMutation,
    setJurisdiction, setSessionState, setPublishChannel, setPublishReason, setRollbackTarget, setRollbackReason,
    updateNavigation, selectComponent, selectCard, updateComponent, updateComponentField, moveComponent, moveComponentTo, addComponent, removeComponent,
    addCard, duplicateCard, duplicateComponent, updateCardField, moveCardTo, removeCard, selectManifest,
  }
}
